import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Seo } from "../components/Seo";
import { PageHero } from "../components/PageHero";
import { Reveal, Eyebrow } from "../components/motion";
import { Process } from "../components/Process";
import { QuoteCTA } from "../components/QuoteCTA";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { CITIES, BRAND, PHONE } from "../lib/site";
import { SERVICES } from "../lib/services";

const ld = (s) => [
  {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: s.nav,
    name: `${s.nav} — ${BRAND}`,
    description: s.metaDesc,
    provider: { "@type": "ProfessionalService", name: BRAND, telephone: `+1-${PHONE}` },
    areaServed: CITIES.map((c) => ({ "@type": "City", name: `${c}, AZ` })),
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: s.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  },
];

const Scope = ({ s }) => (
  <section className="bg-white py-28 md:py-36" data-testid="service-scope">
    <div className="mx-auto grid max-w-[1400px] gap-16 px-5 md:px-10 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <Reveal><Eyebrow>Typical scope</Eyebrow></Reveal>
        <Reveal delay={0.08}><h2 className="mt-6 font-display text-4xl font-semibold leading-[1] tracking-tight text-ink md:text-5xl">What a visit covers.</h2></Reveal>
        <Reveal delay={0.12}><p className="mt-6 max-w-md text-base leading-relaxed text-slate-600">Your final scope is written into your proposal, area by area. This is where most {s.nav.toLowerCase()} contracts start.</p></Reveal>
      </div>
      <ul className="grid gap-px overflow-hidden rounded-[24px] border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:col-span-7">
        {s.scope.map((item, i) => (
          <Reveal as="li" key={item} delay={0.03 * i} className="flex items-start gap-4 bg-white p-6">
            <Check className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
            <span className="text-base text-ink">{item}</span>
          </Reveal>
        ))}
      </ul>
    </div>
  </section>
);

const Fit = ({ s }) => (
  <section className="bg-porcelain py-28 md:py-36" data-testid="service-fit">
    <div className="mx-auto grid max-w-[1400px] gap-16 px-5 md:px-10 lg:grid-cols-2">
      <div>
        <Reveal><Eyebrow>Built for</Eyebrow></Reveal>
        <ul className="mt-8">
          {s.suited.map((x, i) => (
            <Reveal as="li" key={x} delay={0.05 * i} className="border-b border-slate-300 py-5 font-display text-2xl font-medium text-ink md:text-3xl">{x}</Reveal>
          ))}
        </ul>
      </div>
      <div>
        <Reveal><Eyebrow>Scheduling</Eyebrow></Reveal>
        <Reveal delay={0.08}><p className="mt-8 font-display text-2xl leading-snug text-ink md:text-3xl">{s.cadence}</p></Reveal>
        <Reveal delay={0.14}>
          <Link to="/request-quote" data-testid="service-quote-btn" className="btn-ink group mt-10">Request a quote for this service <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></Link>
        </Reveal>
      </div>
    </div>
  </section>
);

const Faq = ({ s }) => (
  <section className="bg-white py-28 md:py-36" data-testid="service-faq">
    <div className="mx-auto grid max-w-[1400px] gap-12 px-5 md:px-10 lg:grid-cols-12">
      <div className="lg:col-span-4">
        <Reveal><Eyebrow>Questions</Eyebrow></Reveal>
        <Reveal delay={0.08}><h2 className="mt-6 font-display text-4xl font-semibold tracking-tight text-ink md:text-5xl">Straight answers.</h2></Reveal>
      </div>
      <Accordion type="single" collapsible className="lg:col-span-8">
        {s.faqs.map((f, i) => (
          <AccordionItem key={f.q} value={`f${i}`} className="border-slate-200">
            <AccordionTrigger data-testid={`faq-trigger-${i}`} className="py-6 text-left font-display text-lg font-medium text-ink hover:no-underline md:text-xl">{f.q}</AccordionTrigger>
            <AccordionContent className="pb-6 text-base leading-relaxed text-slate-600">{f.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);

export default function ServicePage({ slug }) {
  const s = SERVICES.find((x) => x.slug === slug);
  return (
    <>
      <Seo title={s.metaTitle} description={s.metaDesc} path={s.path} jsonLd={ld(s)} />
      <PageHero kicker={`${s.no} — ${s.kicker}`} lines={s.lines} intro={s.intro} image={s.image} alt={s.alt}>
        <Link to="/request-quote" data-testid="service-hero-quote-btn" className="btn-glint group mt-10 !px-7 !py-4">Request a Free Cleaning Quote <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></Link>
      </PageHero>
      <Scope s={s} />
      <Fit s={s} />
      <Process />
      <Faq s={s} />
      <QuoteCTA />
    </>
  );
}
